import * as React from "react";
import { Paper, Tab, Slider, InputAdornment, Stack, IconButton, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TabContext } from "@mui/lab";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bot, Currency } from "../../../_app/types";
import { Order } from "../../../class/Order";
import { RootState } from "../../../store/store";
import {
  calculateStepsCount,
  getBotNotional,
  getMinInvestmentStepCount,
} from "../../../utils/utils";
import { _roundDec } from "../../../utils/_roundToDecimals";
import { precision } from "../../../_utils/precision";
import { _adjustInvestment } from "../../../utils/_adjustInvestment";
import { lockStartBot, unlockStartBot } from "../../../store/slices/userSlice";
import AdronTypography from "../../../_components/AdronTypography";
import { TabsStyled } from "../../../_components/TabsStyled";
import SimpleFlex from "../../../components/ui/SimpleFlex";
import { NumericFormatCustom } from "../../../components/ui/StyledAdron";
import IconComponent from "../../../components/ui/IconComponent";
import { IconButtonStyled } from "../../../_app/consts";
import { MinusIcon, PlusIcon } from "../../../components/ui/icons/adronIcons";
import DottedTextField from "../../../_components/DottedTextField";
import { printProfitPerOrderRange } from "../../../utils/calcBotCreationFields";
import formatNumber from "../../../_utils/formatNumber";
import { TextFieldStyled } from "../../../_components/TextFieldStyled";

interface Props {
  bot: Bot;
  base: string;
  quote: string;
  value: number;
  onChange: (value: number) => void;
  onChangeCurrency: (value: Currency) => void;
  tickerPrice: number;
  stepInQuote: number;
  stepInBase: number;
  onChangeStepsCount: (value: number) => void;
  priceStepQuery: number;
  leverage: number;
  orderList: Order[];
}

export default function FuturesBotInvestment({
  bot,
  base,
  quote,
  value,
  onChange,
  tickerPrice,
  onChangeCurrency,
  stepInBase,
  stepInQuote,
  leverage,
  onChangeStepsCount,
  orderList,
}: Props) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);
  const [currency, setCurrency] = useState<string>(bot.currencyType === "BASE" ? base : quote);

  const minInvestmentSteps = useMemo(() => {
    return getMinInvestmentStepCount(
      bot.ticker.minNotional,
      getBotNotional(bot.minPrice, bot.ticker.orderStep)
    );
  }, [bot.minPrice, bot.ticker.orderStep]);

  const minInitialAmount = useMemo(() => {
    return currency === base ? stepInBase * minInvestmentSteps : stepInQuote * minInvestmentSteps;
  }, [minInvestmentSteps, currency, stepInBase, stepInQuote]);

  const helperTextInvestment = useMemo(() => {
    return value < minInitialAmount
      ? `Minimum allowed value is ${minInitialAmount} ${currency === base ? base : quote}`
      : "";
  }, [minInitialAmount, value]);

  const orderSize = useMemo(() => {
    let steps =
      bot.currencyType === "BASE"
        ? _roundDec(bot.investment / stepInBase, 0)
        : Number((bot.investment / stepInQuote).toFixed(0));
    return _roundDec(bot.ticker.orderStep * steps, precision(bot.ticker.orderStep));
  }, [bot.investment, stepInBase, stepInQuote]);

  const handleInvestment = (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    if (newValue < minInitialAmount) {
      console.log("newValue", minInitialAmount);
      onChange(minInitialAmount as number);
    } else {
      onChange(newValue as number);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrency(newValue);
    onChangeCurrency(newValue === base ? "BASE" : "QUOTE");
    if (newValue === base) {
      onChange(_roundDec(value / stepInQuote, 0) * stepInBase);
    } else {
      onChange((value / stepInBase) * stepInQuote);
    }
  };

  const handleIncreaseStep = () => {
    if (currency === base) {
      // let roundValue = _adjustInvestment(_roundDec(value, Math.abs(Math.log10(priceStepQuery))), stepInBase);
      onChange(value + stepInBase);
    } else {
      // let roundValue = _adjustInvestment(value, stepInQuote);
      onChange(
        _roundDec(value + stepInQuote, precision(bot.ticker.orderStep * bot.ticker.priceStep))
      );
    }
  };

  const handleDecreaseStep = () => {
    if (currency === base) {
      // let roundValue = _adjustInvestment(_roundDec(value, Math.abs(Math.log10(priceStepQuery))), stepInBase);
      onChange(value - stepInBase);
    } else {
      // let roundValue = _adjustInvestment(value, stepInQuote);
      onChange(
        _roundDec(value - stepInQuote, precision(bot.ticker.orderStep * bot.ticker.priceStep))
      );
    }
  };

  const handleOnBlurInvestment = (value: number) => {
    // tempInvestment
    let roundValue = _adjustInvestment(
      _roundDec(value, Math.abs(Math.log10(bot.ticker.priceStep * bot.ticker.orderStep))),
      currency === base ? stepInBase : stepInQuote
    );
    onChangeStepsCount(currency === base ? value / stepInBase : value / stepInQuote);
    onChange(roundValue);
  };

  const calculateSteptsCountAndInvestment = (
    bot: Bot,
    stepInBase: number,
    stepInQuote: number,
    stepsCount: number
  ) => {
    if (stepsCount === Infinity) {
      const adjustedInvestment = _adjustInvestment(
        bot.investment,
        bot.currencyType === "BASE" ? stepInBase : stepInQuote
      );
      const steps =
        bot.currencyType === "BASE"
          ? adjustedInvestment / stepInBase
          : adjustedInvestment / stepInQuote;

      return _roundDec(steps, precision(bot.ticker.orderStep)) * bot.ticker.orderStep;
    }

    return _roundDec(bot.ticker.orderStep * stepsCount, precision(bot.ticker.orderStep));
  };

  const { min, max } = useMemo(() => {
    const result = orderList.reduce<{ min: number; max: number }>(
      (acc, item) => {
        const value = bot.gridType === "ARITHMETIC" ? item.priceStepPercent * 100 : item.priceStep;
        acc.min = Math.min(acc.min, value);
        acc.max = Math.max(acc.max, value);
        return acc;
      },
      { min: Infinity, max: -Infinity }
    );
    return result;
  }, [orderList]);

  const minOrderAmount = useMemo(() => {
    return Math.min(...bot.customGridSettings.flatMap((item) => item.amount));
  }, [bot.customGridSettings]);

  const STEPS_COUNT = useMemo(() => {
    return calculateStepsCount(
      bot,
      stepInBase,
      stepInQuote,
      bot.currencyType === "BASE" ? value / stepInBase : value / stepInQuote
    );
  }, [bot.currencyType, value, stepInBase, stepInQuote]);

  useEffect(() => {
    onChange(_adjustInvestment(value, currency === base ? stepInBase : stepInQuote));
  }, []);

  useEffect(() => {
    if (value < minInitialAmount) {
      dispatch(lockStartBot());
    } else {
      dispatch(unlockStartBot());
    }
  }, [value]);

  return (
    <Paper
      elevation={0}
      className="base-adron-paper paper-light"
      style={{ backgroundColor: theme.palette.grey[400] }}
    >
      <Stack direction={"column"} spacing={"24px"}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <AdronTypography value={"Investment"} halfOpacity />
          <TabContext value={currency}>
            <TabsStyled style={{ borderRadius: 12 }} onChange={handleChange}>
              <Tab
                disabled={CustomGridMode}
                label={base}
                value={base}
                className="AdronSecondaryTabs"
              />
              <Tab
                disabled={CustomGridMode}
                label={quote}
                value={quote}
                className="AdronSecondaryTabs"
              />
            </TabsStyled>
          </TabContext>
        </Stack>
        <SimpleFlex gap={16}>
          <TextFieldStyled
            disabled={CustomGridMode}
            sx={{ textAlign: "right" }}
            fullWidth={true}
            error={value < minInitialAmount}
            helperText={helperTextInvestment}
            label=""
            variant="outlined"
            value={value === 0 ? "" : value}
            size="small"
            onChange={(e) => {
              const value = +e.target.value;
              if (value < 0) {
                onChange(value * -1);
              } else {
                onChange(value);
              }
            }}
            onBlur={() => handleOnBlurInvestment(value)}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              startAdornment: (
                <InputAdornment position="start">
                  <IconComponent iconName={`${currency}`} type={"asBackground"} size={24} />
                </InputAdornment>
              ),
              inputProps: {
                inputMode: "decimal",
                pattern: "[0-9]*",
                decimalCustom:
                  currency === base
                    ? Math.abs(Math.log10(bot.ticker.priceStep * bot.ticker.orderStep))
                    : 2,
              },
              endAdornment: (
                <InputAdornment position="end">
                  <AdronTypography halfOpacity value={`${currency}`} />
                </InputAdornment>
              ),
            }}
            required
          />
          <SimpleFlex gap={8}>
            <IconButton
              sx={{ opacity: value <= minInitialAmount ? 0.4 : 1, ...IconButtonStyled(theme) }}
              disabled={value <= minInitialAmount || CustomGridMode}
              onClick={handleDecreaseStep}
            >
              <MinusIcon />
            </IconButton>
            <IconButton
              sx={IconButtonStyled(theme)}
              disabled={CustomGridMode}
              onClick={handleIncreaseStep}
            >
              <PlusIcon />
            </IconButton>
          </SimpleFlex>
        </SimpleFlex>
        <Stack direction={"column"} justifyContent={"center"} spacing={"2px"}>
          {CustomGridMode ? (
            <DottedTextField placeholder={`Order size`} value={`$${minOrderAmount}`} />
          ) : (
            <DottedTextField placeholder={`Order size`} value={`${orderSize} ${bot.base}`} />
          )}
          {CustomGridMode ? (
            <DottedTextField
              placeholder={`Profit per order`}
              value={printProfitPerOrderRange(bot.customGridSettings)}
            />
          ) : (
            <DottedTextField
              placeholder={`Profit per order`}
              value={
                bot.gridType === "ARITHMETIC"
                  ? `$${formatNumber(bot.priceStep * bot.ticker.orderStep * STEPS_COUNT)}`
                  : `$${formatNumber(min * bot.ticker.orderStep * STEPS_COUNT)} - $${formatNumber(max * bot.ticker.orderStep * STEPS_COUNT)}`
              }
            />
          )}
          <DottedTextField
            placeholder={"Required margin"}
            value={`$${formatNumber(bot.currencyType === "BASE" ? (STEPS_COUNT * stepInQuote) / leverage : value / leverage, 2)}`}
          />
        </Stack>
      </Stack>
      <Slider
        disabled={CustomGridMode}
        size="medium"
        value={value}
        max={currency === base ? 10000 / tickerPrice : 10000} // Current Account Balance
        min={currency === base ? stepInBase * minInvestmentSteps : stepInQuote * minInvestmentSteps}
        step={currency === base ? stepInBase : stepInQuote}
        onChange={handleInvestment}
        onChangeCommitted={handleInvestment}
        valueLabelDisplay="auto"
      />
    </Paper>
  );
}
